import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

export interface LineStep {
  id: string;
  label: string;
}

export type LineStepsProps = {
  steps: LineStep[];
  currentStep: string;
  className?: string;
  navAriaLabel?: string;
  onStepClick?: (stepId: string) => void;
  /** Step ids to force non-clickable even if otherwise completed/active. */
  nonClickableStepIds?: string[];
};

export function LineSteps(props: Readonly<LineStepsProps>) {
  return (
    <nav
      aria-label={props.navAriaLabel || "Line steps navigation"}
      className={cn("w-full", props.className)}
      data-testid="line-steps"
    >
      <ol className="flex w-full items-start">
        {props.steps.map((step, index) => {
          const stepNumber = index + 1;
          const isActive = step.id === props.currentStep;
          const isCompleted =
            index <
            props.steps.findIndex((step) => step.id === props.currentStep);
          const isLast = index === props.steps.length - 1;
          const isClickable =
            (isActive || isCompleted) &&
            !props.nonClickableStepIds?.includes(step.id);

          return (
            <li
              className={cn(
                "flex max-w-[234px] shrink-0 items-start",
                !isLast && "flex-1"
              )}
              key={step.id}
            >
              <div className="flex min-w-[70px] shrink-0 flex-col items-center gap-3">
                <Button
                  aria-label={`step ${stepNumber} of ${props.steps.length}${isCompleted ? ", completed" : ""}`}
                  className={cn(
                    isActive ? "bg-primary text-primary-foreground" : "",
                    isClickable ? "cursor-pointer" : "cursor-default"
                  )}
                  onClick={() => isClickable && props.onStepClick?.(step.id)}
                  size="rounded-lg"
                  variant="rounded"
                >
                  <Text variant="body-lg">{stepNumber}</Text>
                </Button>

                <Text variant="body-md">{step.label}</Text>
              </div>

              {!isLast && (
                <div className="mt-7 h-px min-w-2 max-w-[120px] grow bg-border sm:mx-2 md:mx-[22px]" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
